import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { AppDispatch } from '../../app/store';
import { getBlobWithAuth } from '../../http';
import { showError } from '../notifications/notificationsSlice';
import { Localized } from '@fluent/react';
import { CancelButton } from '../../app/components/CancelButton';


export type PrinterMode = 'download' | 'open'


interface ReportPrinterProps {
    documentId: string;
    language: string;
    mode: PrinterMode;
    reportPath?: string;
    isDialogOpen: boolean;
    closeDialog: () => void;
}

export const ReportPrinter = ({ reportPath, language, mode, isDialogOpen, closeDialog }: ReportPrinterProps) => {
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const dispatch: AppDispatch = useDispatch();

    const handlePrintDocument = async () => {
        setIsGenerating(true);

        try {
            const response = await dispatch(getBlobWithAuth({
                url: `${reportPath}${reportPath?.includes('?') ? '&' : '?'}language=${encodeURIComponent(language)}`,
            })).unwrap()

            if (mode === 'open') {
                window.open(response.blobUrl);
            } else if (mode === 'download') {
                const link = document.createElement('a');
                link.href = response.blobUrl;
                link.setAttribute('download', response.fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setIsGenerating(false);
            closeDialog();
        }
    };

    useEffect(() => {
        if (isDialogOpen) {
            handlePrintDocument();
        }
    }, [isDialogOpen]);

    return (
        <Dialog open={isDialogOpen}>
            <DialogTitle>
                <Localized id="report-generating">
                    <span>_Generating Report</span>
                </Localized>
            </DialogTitle>
            <DialogContent>
                <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                    <CircularProgress />
                    <Localized id="report-generating-description">
                        <span>_Your report is being generated. Please wait...</span>
                    </Localized>
                </Box>
            </DialogContent>
            <DialogActions>
                <CancelButton
                    onClick={closeDialog}
                    disabled={isGenerating}
                    loading={isGenerating} />
            </DialogActions>
        </Dialog>
    );
};